import React from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EventIcon from "@mui/icons-material/Event";

export const InstructorFilterBar = ({
    searchTerm = "",
    onSearchChange,
    counties = [],
    selectedCounty = "Alle",
    onSelectCounty,
    onlyWithUpcomingCourses = false,
    onToggleUpcomingCourses,
    upcomingCoursesCount = 0,
    resultCount = 0,
}) => {
    return (
        <Box sx={{mb: 3, width: "100%", maxWidth: "100%", minWidth: 0, position: "relative"}}>
            {/* Accessible Live Region for Screen Readers */}
            <Box
                role="status"
                aria-live="polite"
                sx={{
                    position: "absolute",
                    width: "1px",
                    height: "1px",
                    padding: 0,
                    margin: "-1px",
                    overflow: "hidden",
                    clip: "rect(0, 0, 0, 0)",
                    whiteSpace: "nowrap",
                    border: 0,
                    left: 0,
                    top: 0,
                }}
            >
                {`Viser ${resultCount} ${resultCount === 1 ? "kursholder" : "kursholdere"}${
                    selectedCounty !== "Alle" ? ` i ${selectedCounty}` : ""
                }${onlyWithUpcomingCourses ? " med planlagte kurs" : ""}`}
            </Box>

            {/* Search Input in Full Width */}
            <Box sx={{mb: 1.5, width: "100%", minWidth: 0}}>
                <TextField
                    fullWidth
                    size="medium"
                    variant="outlined"
                    placeholder="Søk etter kursholder, sted eller fylke..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    inputProps={{
                        "aria-label": "Søk etter kursholder, sted eller fylke",
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm ? (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    aria-label="Tøm søk"
                                    onClick={() => onSearchChange("")}
                                    edge="end"
                                    sx={{
                                        minWidth: 44,
                                        minHeight: 44,
                                        "&:focus-visible": {
                                            outline: "2px solid #285238",
                                            outlineOffset: "2px",
                                        },
                                    }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                    sx={{
                        width: "100%",
                        minWidth: 0,
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&.Mui-focused fieldset": {
                                borderColor: "primary.main",
                                borderWidth: 2,
                            },
                        },
                    }}
                />
            </Box>

            {/* Filter Chips: Upcoming Courses toggle + Counties */}
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    pt: 0.5,
                }}
                role="group"
                aria-label="Filtervalg for kursholdere"
            >
                {typeof onToggleUpcomingCourses === "function" && (
                    <Chip
                        icon={<EventIcon sx={{fontSize: "1.05rem !important"}} />}
                        label={
                            upcomingCoursesCount > 0
                                ? `Kun planlagte kurs (${upcomingCoursesCount})`
                                : "Kun planlagte kurs"
                        }
                        clickable
                        aria-pressed={onlyWithUpcomingCourses}
                        aria-label="Filtrer på kursholdere med planlagte kurs"
                        color={onlyWithUpcomingCourses ? "primary" : "default"}
                        variant={onlyWithUpcomingCourses ? "filled" : "outlined"}
                        onClick={onToggleUpcomingCourses}
                        sx={{
                            fontWeight: onlyWithUpcomingCourses ? 700 : 600,
                            fontSize: "0.875rem",
                            minHeight: 44,
                            height: "auto",
                            py: 0.75,
                            px: 1,
                            borderRadius: 2,
                            borderColor: onlyWithUpcomingCourses ? "primary.main" : "rgba(40, 82, 56, 0.25)",
                            bgcolor: onlyWithUpcomingCourses ? "primary.main" : "rgba(40, 82, 56, 0.04)",
                            color: onlyWithUpcomingCourses ? "#FFFFFF" : "text.primary",
                            transition: "all 0.15s ease-in-out",
                            "& .MuiChip-icon": {
                                color: onlyWithUpcomingCourses ? "#FFFFFF !important" : "primary.main !important",
                            },
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    />
                )}

                {counties.length > 1 &&
                    counties.map(({county, count}) => {
                        const isSelected = selectedCounty === county;
                        return (
                            <Chip
                                key={county}
                                label={`${county} (${count})`}
                                clickable
                                aria-pressed={isSelected}
                                aria-label={`Filtrer på ${county}, ${count} ${
                                    count === 1 ? "kursholder" : "kursholdere"
                                }`}
                                color={isSelected ? "primary" : "default"}
                                variant={isSelected ? "filled" : "outlined"}
                                onClick={() => onSelectCounty(county)}
                                sx={{
                                    fontWeight: isSelected ? 600 : 500,
                                    fontSize: "0.875rem",
                                    minHeight: 44,
                                    height: "auto",
                                    py: 0.75,
                                    px: 1,
                                    borderRadius: 2,
                                    transition: "all 0.15s ease-in-out",
                                    "&:focus-visible": {
                                        outline: "2px solid #285238",
                                        outlineOffset: "2px",
                                    },
                                }}
                            />
                        );
                    })}
            </Box>
        </Box>
    );
};
